import { appConstants, changeLanguage as i18nChangeLanguage, extend } from "@abp";
import {
  AuthenticationRequest,
  AuthenticationResponse,
  ExternalAuthenticateRequest,
  ExternalAuthenticateResponse,
} from "@models";
import { authenticationService, userService } from "@services";
import dayjs from "dayjs";
import { makeAutoObservable, runInAction } from "mobx";

declare const abp: any;

const ABP_CONFIG_WAIT_MS = 8000;

export default class AuthenticationStore {
  loading: boolean = false;
  localizationReady: boolean = false;
  currentUserId: number | undefined;

  constructor() {
    makeAutoObservable(this);
    const hadCache = this.loadAbpData();
    if (hadCache) this.localizationReady = true;
    this.loadCurrentUserId();
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  authenticate = async (
    request: AuthenticationRequest
  ): Promise<AuthenticationResponse | undefined> => {
    this.setLoading(true);
    try {
      const response = await authenticationService.authenticate(request);
      await this.storeTokens(response);
      return response;
    } catch (error) {
      console.error("Authentication error:", error);
      return undefined;
    } finally {
      this.setLoading(false);
    }
  };

  externalAuthenticate = async (
    request: ExternalAuthenticateRequest
  ): Promise<ExternalAuthenticateResponse | undefined> => {
    this.setLoading(true);
    try {
      const response = await authenticationService.externalAuthenticate(request);

      if (response.requiresRegistration) {
        return response;
      }

      if (response.authenticateResult) {
        await this.storeTokens(response.authenticateResult);
      }

      return response;
    } catch (error) {
      console.error("External authentication error:", error);
      return undefined;
    } finally {
      this.setLoading(false);
    }
  };

  private storeTokens = async (response: AuthenticationResponse) => {
    const tokenExpireDate = dayjs()
      .add(response.expireInSeconds, "second")
      .toDate();

    abp.auth.setToken(response.accessToken, tokenExpireDate);

    abp.utils.setCookieValue(
      appConstants.authorization.encrptedAuthTokenName,
      response.encryptedAccessToken,
      tokenExpireDate,
      abp.appPath
    );

    const result = await authenticationService.getAbpUserConfiguration();
    extend(true, abp, result);

    this.setCurrentUserId(response.userId);

    localStorage.setItem("abpData", JSON.stringify(abp));
    localStorage.setItem("tokenExpiryDate", tokenExpireDate.toISOString());
    localStorage.setItem("currentUserId", JSON.stringify(this.currentUserId));
  };

  verifyTurnstileToken: (token: string | null) => Promise<boolean> = async (
    token
  ) => {
    this.setLoading(true);
    try {
      await authenticationService.verifyTurnstileToken(token);
      this.setLoading(false);
      return true;
    } catch {
      this.setLoading(false);
      return false;
    }
  };

  setCurrentUserId = (userId: number | undefined) => {
    runInAction(() => {
      this.currentUserId = userId;
    });
  };

  loadAbpData(): boolean {
    const abpData = localStorage.getItem("abpData");

    if (!abpData) return false;

    extend(true, abp, JSON.parse(abpData));
    return true;
  }

  initAbpData = async () => {
    // Zahtev koji visi (npr. u ugrađenom Instagram pregledaču) ne sme da drži
    // korisnika na spineru: posle ABP_CONFIG_WAIT_MS se aplikacija pokreće sa
    // keširanim podacima, a odgovor se ipak primeni kad stigne.
    const releaseTimer = window.setTimeout(
      () =>
        runInAction(() => {
          this.localizationReady = true;
        }),
      ABP_CONFIG_WAIT_MS,
    );

    try {
      const result = await authenticationService.getAbpUserConfiguration();
      extend(true, abp, result);
      localStorage.setItem("abpData", JSON.stringify(abp));
    } catch {
      // silently fail, use cached localStorage data
    } finally {
      window.clearTimeout(releaseTimer);
      runInAction(() => {
        this.localizationReady = true;
      });
    }
  };

  changeLanguage = (lang: string) => {
    // i18n handles language switching reactively — no reload needed.
    // The cookie is updated automatically via the i18n languageChanged event.
    i18nChangeLanguage(lang);
  };

  loadCurrentUserId() {
    const currentUserId = localStorage.getItem("currentUserId");

    if (!currentUserId) return;

    runInAction(() => {
      this.currentUserId = JSON.parse(currentUserId);
    });
  }

  async logout() {
    // Unregister the Firebase token for this device while we still have a valid JWT,
    // so push notifications stop arriving after logout. The token in localStorage
    // is the exact endpoint FCM will push to, so we delete by its value.
    const fcmToken = localStorage.getItem("fcmToken");
    if (fcmToken) {
      try {
        await userService.deleteFirebaseToken(fcmToken);
      } catch {
        // Tolerate failure — better to log the user out than block on an unregister.
      }
    }

    document.cookie =
      "RasporediMe-AuthToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Strict";
    document.cookie =
      "enc_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Strict";
    localStorage.clear();
    sessionStorage.clear();
    abp.auth.clearToken();
    abp.session.userId = null;
    abp.session.tenantId = null;

    runInAction(() => {
      this.currentUserId = undefined;
    });
  }
}
